<template>
  <div data-cy="connection-point-area" v-if="areaInformation !== null">
    <div
      :class="{ 'lib__feedback--danger': block.style === 'orange', 'lib__feedback--success': block.style === 'green' }"
      :key="block.style" class="lib__feedback lib__feedback--simple" v-for="block in energyManagerMessages">
      <div class="lib__feedback__icon">
        <i :class="block.logo"></i>
      </div>
      <div class="lib__feedback__content">
        <p :class="block.style" :key="messageLine" class="lib__no-margin" v-for="messageLine in block.messages"
          v-html="$t(messageLine)"></p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { SubCity } from '@/models'
import { cmhService } from '@/services'
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'

interface Styles {
  style: 'green' | 'orange'
  logo: string
  messages: string[]
}

@Component
export default class ConnectionPointArea extends Vue {
  //#region [Property]
  @Prop(Object) public readonly subCity!: SubCity | null
  @Prop(Boolean) public readonly checkForGas?: boolean
  @Prop(Boolean) public readonly checkForElec?: boolean
  @Prop(Boolean) public readonly hideSuccessMessages?: boolean
  @Prop(Boolean) public readonly showSynergridLink?: boolean
  @Prop(Boolean) public readonly hideOresManagerMessage?: boolean
  //#endregion

  //#region [Data]
  public loading: boolean = false
  public areaInformation: SubCity | null = null

  //#endregion

  //#region [Computed]
  get isGasManaged() {
    return cmhService.isGasManaged(this.areaInformation)
  }

  get isElectrictyManaged() {
    return cmhService.isElectricManagerOres(this.areaInformation)
  }

  get energyManagerMessages(): Styles[] {
    // [ { style : « orange/green », logo : « icon-elec/icon-gaz/icon-both », messages : [ …, …] }
    if (!this.checkForElec && !this.checkForGas) {
      return []
    }
    const info = this.areaInformation
    if (!info) {
      return []
    }

    this.$emit('canAllowGas', this.isGasManaged)
    this.$emit('canAllowElec', this.isElectrictyManaged)
    this.$emit('canAllowElecOrGas', this.isGasManaged || this.isElectrictyManaged)

    // 1. operator?
    if (!info.gridOperators || !info.gridOperators.isInOresScope) {
      if (this.checkForElec && this.checkForGas) {
        return [
          {
            style: 'orange',
            logo: 'fas fa-thumbs-down',
            messages: ['oresNetwork.oresElecGasNotManager', 'oresNetwork.findManager']
          }
        ]
      } else if (this.checkForElec) {
        // check for elec only
        return [
          {
            style: 'orange',
            logo: 'fas fa-thumbs-down',
            messages: ['oresNetwork.oresElecNotManager', 'oresNetwork.findManager']
          }
        ]
      } else {
        // check for gas only
        return [
          {
            style: 'orange',
            logo: 'fas fa-thumbs-down',
            messages: ['oresNetwork.oresGasNotManager', 'oresNetwork.findManager']
          }
        ]
      }
    }

    // ORES is operator
    if (this.checkForElec) {
      // 2. electricity manager ?
      if (this.isElectrictyManaged) {
        if (this.checkForGas) {
          // 3. gas manager ?
          if (this.isGasManaged) {
            if (!this.hideSuccessMessages) {
              return [{ style: 'green', logo: 'fas fa-thumbs-up', messages: ['oresNetwork.oresElecGasManager'] }]
            }
          } else {
            // ores not gas manager
            if (this.hideSuccessMessages) {
              return [
                {
                  style: 'orange',
                  logo: 'fas fa-thumbs-down',
                  messages: ['oresNetwork.oresGasNotManager', 'oresNetwork.findManager']
                }
              ]
            } else {
              return [
                { style: 'green', logo: 'fas fa-thumbs-up', messages: ['oresNetwork.oresElecManager'] },
                {
                  style: 'orange',
                  logo: 'fas fa-thumbs-down',
                  messages: ['oresNetwork.oresGasNotManager', 'oresNetwork.findManager']
                }
              ]
            }
          }
        } else {
          // don't check for gas
          if (!this.hideSuccessMessages) {
            return [{ style: 'green', logo: 'fas fa-thumbs-up', messages: ['oresNetwork.oresElecManager'] }]
          }
        }
      } else {
        // ores not elec manager
        if (this.checkForGas) {
          // 3. gas manager ?
          if (this.isGasManaged) {
            if (this.hideSuccessMessages) {
              return [
                {
                  style: 'orange',
                  logo: 'fas fa-thumbs-down',
                  messages: ['oresNetwork.oresElecNotManager', 'oresNetwork.findManager']
                }
              ]
            } else {
              return [
                {
                  style: 'orange',
                  logo: 'fas fa-thumbs-down',
                  messages: ['oresNetwork.oresElecNotManager', 'oresNetwork.findManager']
                },
                { style: 'green', logo: 'fas fa-thumbs-up', messages: ['oresNetwork.oresGasManager'] }
              ]
            }
          } else {
            // not gas manager
            return [
              {
                style: 'orange',
                logo: 'fas fa-thumbs-down',
                messages: ['oresNetwork.oresElecGasNotManager', 'oresNetwork.findManager']
              }
            ]
          }
        } else {
          return [
            {
              style: 'orange',
              logo: 'fas fa-thumbs-down',
              messages: ['oresNetwork.oresElecNotManager', 'oresNetwork.findManager']
            }
          ]
        }
      }
    } else {
      // no check for elec
      if (this.checkForGas) {
        // 3. gas manager ?
        if (this.isGasManaged) {
          if (!this.hideSuccessMessages) {
            return [{ style: 'green', logo: 'fas fa-thumbs-up', messages: ['oresNetwork.oresGasManager'] }]
          }
        } else {
          // ores not gas manager
          return [
            {
              style: 'orange',
              logo: 'fas fa-thumbs-down',
              messages: ['oresNetwork.oresGasNotManager', 'oresNetwork.findManager']
            }
          ]
        }
      }
    }

    return []
  }
  //#endregion

  //#region [Watch]
  @Watch('subCity')
  public onSubCityChanged(subCity: SubCity | null) {
    this.$nextTick(() => {
      this.fetchAreaInformation()
    })
  }
  //#endregion

  //#region [Method]
  public mounted(): void {
    if (this.subCity !== null) {
      this.fetchAreaInformation()
    }
  }

  public fetchAreaInformation(): void {
    if (this.subCity === null) {
      this.areaInformation = null
      return
    }

    this.areaInformation = this.subCity
  }
  //#endregion
}
</script>